/**
 * useAudioManager Hook
 *
 * React hook that wraps AudioService for React components.
 * Handles:
 * - Audio service initialization on mount
 * - Document unlock listener for mobile browsers
 * - Route-based music switching (landing vs gameplay)
 * - Settings store subscription for real-time toggle response
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAudioService, type SFXType } from '@/shared/services/AudioService';
import { useSettingsStore } from '@/shared/stores/useSettingsStore';

/**
 * Volume levels for different contexts
 */
const LANDING_MUSIC_VOLUME = 1.0;
const GAMEPLAY_MUSIC_VOLUME = 0.15;

interface UseAudioManagerReturn {
  playSFX: (effect: SFXType) => void;
  isAudioReady: boolean;
}

/**
 * Audio manager hook for React components
 */
export function useAudioManager(): UseAudioManagerReturn {
  const location = useLocation();
  const audioServiceRef = useRef(getAudioService());
  const [isAudioReady, setIsAudioReady] = useState(() => audioServiceRef.current.isReady);

  // Get initial settings values
  const musicEnabled = useSettingsStore((state) => state.musicEnabled);
  const soundEffectsEnabled = useSettingsStore((state) => state.soundEffectsEnabled);

  // Track previous route for comparison
  const previousPathnameRef = useRef<string | null>(null);

  // Initialize AudioService on mount
  useEffect(() => {
    const audioService = audioServiceRef.current;

    // Setup document unlock listener for mobile browsers
    audioService.setupUnlockListener();

    // Register callback for when audio is ready
    audioService.onReady(() => {
      setIsAudioReady(true);
    });

    // Apply initial settings state
    audioService.muteMusic(!musicEnabled);
    audioService.muteSFX(!soundEffectsEnabled);
  }, []); // Only run once on mount

  // Handle route-based music switching
  useEffect(() => {
    const audioService = audioServiceRef.current;
    const currentPathname = location.pathname;

    // Skip if pathname hasn't changed
    if (previousPathnameRef.current === currentPathname) {
      return;
    }
    previousPathnameRef.current = currentPathname;

    // Simplified route detection: /game covers /games, /game/, /game/123
    const isGameplayRoute = currentPathname.startsWith('/game');

    if (isGameplayRoute) {
      audioService.setMusicVolume(GAMEPLAY_MUSIC_VOLUME);
      audioService.playMusic('rolling');
    } else {
      audioService.setMusicVolume(LANDING_MUSIC_VOLUME);
      audioService.playMusic('welcome');
    }
  }, [location.pathname]);

  // Subscribe to settings store changes
  useEffect(() => {
    const audioService = audioServiceRef.current;

    const unsubscribe = useSettingsStore.subscribe((state, prevState) => {
      if (state.musicEnabled !== prevState.musicEnabled) {
        audioService.muteMusic(!state.musicEnabled);
      }

      if (state.soundEffectsEnabled !== prevState.soundEffectsEnabled) {
        audioService.muteSFX(!state.soundEffectsEnabled);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Memoized playSFX function
  const playSFX = useCallback((effect: SFXType) => {
    audioServiceRef.current.playSFX(effect);
  }, []);

  return {
    playSFX,
    isAudioReady
  };
}
